import React, { useState } from 'react';

interface CompanyInfoData {
  carrierName: string;
  mcNumber: string;
  dotNumber: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
}

interface Step1Props {
  data: CompanyInfoData;
  onNext: () => void;
  onSave: (data: CompanyInfoData) => void;
}

const Step1CompanyInfo: React.FC<Step1Props> = ({ data, onNext, onSave }) => {
  const [formData, setFormData] = useState<CompanyInfoData>(data);
  const [errors, setErrors] = useState<Partial<CompanyInfoData>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name as keyof CompanyInfoData]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors: Partial<CompanyInfoData> = {};
    
    if (!formData.carrierName.trim()) {
      newErrors.carrierName = 'Carrier name is required';
    }
    if (!formData.mcNumber.trim()) {
      newErrors.mcNumber = 'MC Number is required';
    } else if (!/^\d{6,7}$/.test(formData.mcNumber.replace(/\D/g, ''))) {
      newErrors.mcNumber = 'MC Number must be 6-7 digits';
    }
    if (!formData.dotNumber.trim()) {
      newErrors.dotNumber = 'DOT Number is required';
    } else if (!/^\d{5,8}$/.test(formData.dotNumber.replace(/\D/g, ''))) {
      newErrors.dotNumber = 'DOT Number must be 5-8 digits';
    }
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    }
    if (!formData.zipCode.trim()) {
      newErrors.zipCode = 'ZIP code is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validate()) {
      onSave(formData);
      onNext();
    }
  };

  const states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  return (
    <div>
      <h2 className="text-xl font-semibold mb-6">Company Information</h2>
      <p className="text-gray-600 mb-6">
        Enter your carrier's basic information. This must match your FMCSA registration.
      </p>

      <div className="space-y-6">
        {/* Carrier Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Carrier / Company Name *
          </label>
          <input
            type="text"
            name="carrierName"
            value={formData.carrierName}
            onChange={handleChange}
            placeholder="e.g., ABC Trucking LLC"
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.carrierName ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.carrierName && (
            <p className="text-red-500 text-sm mt-1">{errors.carrierName}</p>
          )}
        </div>

        {/* MC and DOT Numbers */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              MC Number *
            </label>
            <div className="flex">
              <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                MC-
              </span>
              <input
                type="text"
                name="mcNumber"
                value={formData.mcNumber}
                onChange={handleChange}
                placeholder="123456"
                maxLength={7}
                className={`flex-1 px-3 py-2 border rounded-r-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.mcNumber ? 'border-red-500' : 'border-gray-300'
                }`}
              />
            </div>
            {errors.mcNumber && (
              <p className="text-red-500 text-sm mt-1">{errors.mcNumber}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              DOT Number *
            </label>
            <div className="flex">
              <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                DOT-
              </span>
              <input
                type="text"
                name="dotNumber"
                value={formData.dotNumber}
                onChange={handleChange}
                placeholder="1234567"
                maxLength={8}
                className={`flex-1 px-3 py-2 border rounded-r-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.dotNumber ? 'border-red-500' : 'border-gray-300'
                }`}
              />
            </div>
            {errors.dotNumber && (
              <p className="text-red-500 text-sm mt-1">{errors.dotNumber}</p>
            )}
          </div>
        </div>

        {/* Address */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Street Address *
          </label>
          <input
            type="text"
            name="address"
            value={formData.address}
            onChange={handleChange}
            placeholder="123 Main Street"
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.address ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.address && (
            <p className="text-red-500 text-sm mt-1">{errors.address}</p>
          )}
        </div>

        {/* City, State, ZIP */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              City *
            </label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="City"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.city ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.city && (
              <p className="text-red-500 text-sm mt-1">{errors.city}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              State *
            </label>
            <select
              name="state"
              value={formData.state}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.state ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select State</option>
              {states.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
            {errors.state && (
              <p className="text-red-500 text-sm mt-1">{errors.state}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ZIP Code *
            </label>
            <input
              type="text"
              name="zipCode"
              value={formData.zipCode}
              onChange={handleChange}
              placeholder="12345"
              maxLength={10}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.zipCode ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.zipCode && (
              <p className="text-red-500 text-sm mt-1">{errors.zipCode}</p>
            )}
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Your MC and DOT numbers will be verified against FMCSA records. 
            Please ensure they match your official registration.
          </p>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-end mt-8">
        <button
          onClick={handleNext}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Next →
        </button>
      </div>
    </div>
  );
};

export default Step1CompanyInfo;